import React, { useState, useEffect, useMemo } from 'react';
import { MessageSquare, Send, RefreshCw, Filter, Facebook, Instagram, Youtube, Video, CheckCircle, Clock, AlertCircle, ExternalLink, ThumbsUp, X, Search, Archive, ArchiveRestore, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';

interface CommentEvent {
  id: string;
  account_id: string;
  platform: 'facebook' | 'instagram' | 'youtube' | 'tiktok';
  post_id: string;
  video_id: string | null;
  comment_id: string;
  parent_comment_id: string | null;
  post_permalink: string | null;
  from_user: {
    id: string;
    name: string;
  } | null;
  text: string;
  handled: boolean;
  our_reply_id: string | null;
  our_reply_text?: string | null;
  created_at: string;
  raw: any;
  is_reply_to_other_user?: boolean;
  mentioned_user?: string | null;
  queue_status?: string | null;
  queue_tries?: number;
  queue_max_retries?: number;
  queue_last_error?: string | null;
  queue_id?: string | null;
  is_archived?: boolean;
}

interface SocialAccount {
  id: string;
  platform: string;
  page_name: string;
  access_token: string;
}

export function SocialComments() {
  const [comments, setComments] = useState<CommentEvent[]>([]);
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedQueueStatus, setSelectedQueueStatus] = useState<string>('all');
  const [selectedAccount, setSelectedAccount] = useState<string>('all');
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [archivingId, setArchivingId] = useState<string | null>(null);
  const [selectedComments, setSelectedComments] = useState<Set<string>>(new Set());
  const [bulkArchiving, setBulkArchiving] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadData();
  }, [selectedPlatform, selectedStatus, selectedQueueStatus, selectedAccount, showArchived]);

  /**
   * Extracts clean reply text from a potentially nested JSON string.
   * n8n sometimes returns AI responses as JSON with fields like "komentarz", "uzasadnienie", etc.
   */
  const extractCleanReplyText = (text: string | null | undefined): string | null => {
    if (!text) return null;

    const trimmed = text.trim();
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        const parsed = JSON.parse(trimmed);
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
          const commentText =
            parsed.komentarz || parsed.comment || parsed.reply || parsed.text ||
            parsed.message || parsed.response || parsed.odpowiedz || parsed.tresc || parsed.content;
          if (typeof commentText === 'string' && commentText.trim().length > 0) {
            return commentText.trim();
          }
          const stringFields = Object.entries(parsed).filter(
            ([_, v]) => typeof v === 'string' && (v as string).length > 20
          );
          if (stringFields.length === 1) {
            return (stringFields[0][1] as string).trim();
          }
        }
        if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'object') {
          const first = parsed[0];
          const commentText = first.komentarz || first.comment || first.text || first.message || first.reply;
          if (typeof commentText === 'string' && commentText.trim().length > 0) {
            return commentText.trim();
          }
        }
      } catch {
        // Not valid JSON
      }
    }

    return text;
  };

  const loadData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: accountsData } = await supabase
        .from('social_accounts')
        .select('*')
        .eq('owner_user_id', user.id);

      setAccounts(accountsData || []);

      let accountIds = (accountsData || []).map(a => a.id);
      if (selectedAccount !== 'all') {
        accountIds = [selectedAccount];
      }

      let query = supabase
        .from('comment_events')
        .select('*')
        .in('account_id', accountIds)
        .eq('is_archived', showArchived)
        .or('is_our_own_comment.is.null,is_our_own_comment.eq.false')
        .order('created_at', { ascending: false })
        .limit(200);

      if (selectedPlatform !== 'all') {
        query = query.eq('platform', selectedPlatform);
      }

      if (selectedStatus === 'handled') {
        query = query.eq('handled', true);
      } else if (selectedStatus === 'pending') {
        query = query.eq('handled', false);
      }

      const { data, error } = await query;

      if (error) throw error;

      const commentsWithReplies = await Promise.all(
        (data || []).map(async (comment) => {
          const { data: queueData } = await supabase
            .from('outbound_queue')
            .select('id, response_payload, status, tries, max_retries, last_error')
            .eq('ref_id', comment.id)
            .eq('event_type', 'comment.inbound')
            .order('created_at', { ascending: false })
            .maybeSingle();

          let replyText = comment.our_reply_text || null;

          if (queueData) {
            if (!replyText && queueData.response_payload) {
              replyText =
                queueData.response_payload.message?.text ||
                queueData.response_payload[0]?.message?.text ||
                queueData.response_payload.reply?.text;
            }

            // Clean up JSON-formatted AI responses
            replyText = extractCleanReplyText(replyText);

            return {
              ...comment,
              our_reply_text: replyText,
              queue_status: queueData.status,
              queue_tries: queueData.tries,
              queue_max_retries: queueData.max_retries,
              queue_last_error: queueData.last_error,
              queue_id: queueData.id,
            };
          }

          // Clean up JSON-formatted AI responses
          replyText = extractCleanReplyText(replyText);

          return {
            ...comment,
            our_reply_text: replyText,
          };
        })
      );

      setComments(commentsWithReplies);
      setSelectedComments(new Set());
    } catch (error) {
      console.error('Error loading comments:', error);
      toast.error('Nie udalo sie zaladowac komentarzy');
    } finally {
      setLoading(false);
    }
  };

  const filteredComments = useMemo(() => {
    let result = comments;

    if (selectedQueueStatus !== 'all') {
      result = result.filter(c => {
        if (selectedQueueStatus === 'pending_approval') return c.queue_status === 'PENDING_APPROVAL';
        if (selectedQueueStatus === 'dlq') return c.queue_status === 'DLQ';
        if (selectedQueueStatus === 'queued') return c.queue_status === 'QUEUED' || c.queue_status === 'PROCESSING';
        if (selectedQueueStatus === 'sent') return c.queue_status === 'SENT' || c.queue_status === 'ACKED';
        if (selectedQueueStatus === 'no_queue') return !c.queue_status;
        return true;
      });
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(c =>
        c.text?.toLowerCase().includes(query) ||
        c.from_user?.name?.toLowerCase().includes(query) ||
        c.our_reply_text?.toLowerCase().includes(query) ||
        c.comment_id?.toLowerCase().includes(query)
      );
    }

    return result;
  }, [comments, selectedQueueStatus, searchQuery]);

  const handleRetry = async (comment: CommentEvent) => {
    try {
      const account = accounts.find(a => a.id === comment.account_id);
      if (!account) {
        toast.error('Nie znaleziono konta');
        return;
      }

      const { error: updateError } = await supabase
        .from('outbound_queue')
        .update({
          status: 'QUEUED',
          tries: 0,
          last_error: null,
        })
        .eq('ref_id', comment.id)
        .eq('event_type', 'comment.inbound');

      if (updateError) throw updateError;

      await supabase
        .from('comment_events')
        .update({ handled: false })
        .eq('id', comment.id);

      toast.success('Komentarz dodany ponownie do kolejki');
      loadData();
    } catch (error) {
      console.error('Error retrying comment:', error);
      toast.error('Nie udalo sie ponowic proby');
    }
  };

  const handleApprove = async (comment: CommentEvent) => {
    if (!comment.queue_id) {
      toast.error('Brak identyfikatora kolejki');
      return;
    }

    setApprovingId(comment.id);
    try {
      const { data, error } = await supabase.rpc('approve_pending_comment', {
        p_queue_id: comment.queue_id,
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Nieznany blad');
      }

      toast.success('Komentarz zatwierdzony do publikacji');
      loadData();
    } catch (error) {
      console.error('Error approving comment:', error);
      toast.error('Nie udalo sie zatwierdzic komentarza');
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (comment: CommentEvent) => {
    if (!comment.queue_id) {
      toast.error('Brak identyfikatora kolejki');
      return;
    }

    setRejectingId(comment.id);
    try {
      const { data, error } = await supabase.rpc('reject_pending_comment', {
        p_queue_id: comment.queue_id,
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Nieznany blad');
      }

      toast.success('Komentarz odrzucony');
      loadData();
    } catch (error) {
      console.error('Error rejecting comment:', error);
      toast.error('Nie udalo sie odrzucic komentarza');
    } finally {
      setRejectingId(null);
    }
  };

  const handleArchive = async (comment: CommentEvent) => {
    setArchivingId(comment.id);
    try {
      const { data, error } = await supabase.rpc('archive_comment', {
        p_comment_id: comment.id,
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Nieznany blad');
      }

      toast.success('Komentarz zarchiwizowany');
      loadData();
    } catch (error) {
      console.error('Error archiving comment:', error);
      toast.error('Nie udalo sie zarchiwizowac komentarza');
    } finally {
      setArchivingId(null);
    }
  };

  const handleUnarchive = async (comment: CommentEvent) => {
    setArchivingId(comment.id);
    try {
      const { data, error } = await supabase.rpc('unarchive_comment', {
        p_comment_id: comment.id,
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Nieznany blad');
      }

      toast.success('Komentarz przywrocony');
      loadData();
    } catch (error) {
      console.error('Error unarchiving comment:', error);
      toast.error('Nie udalo sie przywrocic komentarza');
    } finally {
      setArchivingId(null);
    }
  };

  const handleBulkArchive = async () => {
    if (selectedComments.size === 0) {
      toast.error('Wybierz komentarze do archiwizacji');
      return;
    }

    setBulkArchiving(true);
    try {
      const { data, error } = await supabase.rpc('bulk_archive_comments', {
        p_comment_ids: Array.from(selectedComments),
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Nieznany blad');
      }

      toast.success(data.message);
      setSelectedComments(new Set());
      loadData();
    } catch (error) {
      console.error('Error bulk archiving:', error);
      toast.error('Nie udalo sie zarchiwizowac komentarzy');
    } finally {
      setBulkArchiving(false);
    }
  };

  const toggleCommentSelection = (commentId: string) => {
    const newSelection = new Set(selectedComments);
    if (newSelection.has(commentId)) {
      newSelection.delete(commentId);
    } else {
      newSelection.add(commentId);
    }
    setSelectedComments(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedComments.size === filteredComments.length) {
      setSelectedComments(new Set());
    } else {
      setSelectedComments(new Set(filteredComments.map(c => c.id)));
    }
  };

  const handleReply = async (comment: CommentEvent) => {
    if (!replyText.trim()) {
      toast.error('Wpisz tresc odpowiedzi');
      return;
    }

    setSendingReply(true);
    try {
      const account = accounts.find(a => a.id === comment.account_id);
      if (!account) throw new Error('Account not found');

      let replyId: string;

      if (comment.platform === 'youtube') {
        const parentId = comment.parent_comment_id || comment.comment_id;

        const response = await fetch(
          `https://www.googleapis.com/youtube/v3/comments?part=snippet`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${account.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              snippet: {
                parentId: parentId,
                textOriginal: replyText,
              },
            }),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`YouTube API error: ${errorText}`);
        }

        const data = await response.json();
        replyId = data.id;
      } else if (comment.platform === 'instagram') {
        const response = await fetch(
          `https://graph.facebook.com/v21.0/${comment.comment_id}/replies?access_token=${account.access_token}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ message: replyText }),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Instagram API error: ${errorText}`);
        }

        const data = await response.json();
        replyId = data.id;
      } else if (comment.platform === 'tiktok') {
        const videoId = comment.video_id;
        const parentCommentId = comment.parent_comment_id;

        const response = await fetch(
          'https://open.tiktokapis.com/v2/video/comment/reply/',
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${account.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              video_id: videoId,
              comment_id: parentCommentId || comment.comment_id,
              text: replyText,
            }),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`TikTok API error: ${errorText}`);
        }

        const data = await response.json();
        replyId = data.data?.comment_id || data.data?.id;
      } else {
        const response = await fetch(
          `https://graph.facebook.com/v21.0/${comment.comment_id}/comments?access_token=${account.access_token}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ message: replyText }),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Facebook API error: ${errorText}`);
        }

        const data = await response.json();
        replyId = data.id;
      }

      await supabase
        .from('comment_events')
        .update({
          handled: true,
          our_reply_id: replyId,
          our_reply_text: replyText,
        })
        .eq('id', comment.id);

      toast.success('Odpowiedz wyslana');
      setReplyingTo(null);
      setReplyText('');
      loadData();
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Nie udalo sie wyslac odpowiedzi');
    } finally {
      setSendingReply(false);
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'facebook':
        return <Facebook className="w-4 h-4 text-blue-500" />;
      case 'instagram':
        return <Instagram className="w-4 h-4 text-pink-500" />;
      case 'instagram_direct':
        return <Instagram className="w-4 h-4 text-pink-400" />;
      case 'youtube':
        return <Youtube className="w-4 h-4 text-red-500" />;
      case 'tiktok':
        return <Video className="w-4 h-4 text-white" />;
      default:
        return <MessageSquare className="w-4 h-4" />;
    }
  };

  const getStatusBadge = (comment: CommentEvent) => {
    if (comment.queue_status === 'PENDING_APPROVAL') {
      return (
        <span className="inline-flex items-center space-x-1 px-2 py-1 bg-orange-900/30 text-orange-400 text-xs rounded-full">
          <Clock className="w-3 h-3" />
          <span>Oczekuje na zatwierdzenie</span>
        </span>
      );
    }
    if (comment.queue_status === 'DLQ') {
      return (
        <span className="inline-flex items-center space-x-1 px-2 py-1 bg-red-900/30 text-red-400 text-xs rounded-full">
          <AlertCircle className="w-3 h-3" />
          <span>Niepowodzenie ({comment.queue_tries}/{comment.queue_max_retries})</span>
        </span>
      );
    }
    if (comment.is_reply_to_other_user) {
      return (
        <span className="inline-flex items-center space-x-1 px-2 py-1 bg-blue-900/30 text-blue-400 text-xs rounded-full">
          <MessageSquare className="w-3 h-3" />
          <span>Odpowiedz do @{comment.mentioned_user}</span>
        </span>
      );
    }
    if (comment.handled) {
      return (
        <span className="inline-flex items-center space-x-1 px-2 py-1 bg-green-900/30 text-green-400 text-xs rounded-full">
          <CheckCircle className="w-3 h-3" />
          <span>Odpowiedziano</span>
        </span>
      );
    }
    return (
      <span className="inline-flex items-center space-x-1 px-2 py-1 bg-yellow-900/30 text-yellow-400 text-xs rounded-full">
        <Clock className="w-3 h-3" />
        <span>Oczekuje</span>
      </span>
    );
  };

  const getPostUrl = (comment: CommentEvent) => {
    if (comment.post_permalink) {
      return comment.post_permalink;
    }

    const videoId = comment.video_id || comment.post_id;

    switch (comment.platform) {
      case 'youtube':
        return `https://www.youtube.com/watch?v=${videoId}&lc=${comment.comment_id}`;
      case 'facebook':
        return `https://www.facebook.com/${comment.post_id}`;
      case 'instagram':
        return null;
      case 'tiktok':
        return `https://www.tiktok.com/@username/video/${videoId}`;
      default:
        return null;
    }
  };

  const activeFiltersCount = [
    selectedPlatform !== 'all',
    selectedStatus !== 'all',
    selectedQueueStatus !== 'all',
    selectedAccount !== 'all',
    showArchived,
  ].filter(Boolean).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#00C853]" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-white">Komentarze</h2>
          <p className="text-sm sm:text-base text-gray-400 mt-1">
            Przegladaj i odpowiadaj na komentarze z wszystkich platform
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedComments.size > 0 && !showArchived && (
            <button
              onClick={handleBulkArchive}
              disabled={bulkArchiving}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 transition-colors"
            >
              {bulkArchiving ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Archive className="w-4 h-4" />
              )}
              <span>Archiwizuj ({selectedComments.size})</span>
            </button>
          )}
          <button
            onClick={loadData}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="text-sm sm:text-base">Odswiez</span>
          </button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Szukaj w komentarzach, autorach, odpowiedziach..."
          className="w-full pl-10 pr-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:ring-2 focus:ring-[#00C853] focus:border-transparent"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-750 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Filter className="w-5 h-5 text-gray-400" />
            <span className="text-white font-medium">Filtry</span>
            {activeFiltersCount > 0 && (
              <span className="px-2 py-0.5 bg-[#00C853] text-white text-xs rounded-full">
                {activeFiltersCount}
              </span>
            )}
          </div>
          {showFilters ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {showFilters && (
          <div className="p-4 pt-0 border-t border-gray-700 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Platforma</label>
                <select
                  value={selectedPlatform}
                  onChange={(e) => setSelectedPlatform(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white text-sm rounded-lg border border-gray-600 focus:ring-2 focus:ring-[#00C853] focus:border-transparent"
                >
                  <option value="all">Wszystkie platformy</option>
                  <option value="facebook">Facebook</option>
                  <option value="instagram">Instagram</option>
                  <option value="youtube">YouTube</option>
                  <option value="tiktok">TikTok</option>
                </select>
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-1">Konto</label>
                <select
                  value={selectedAccount}
                  onChange={(e) => setSelectedAccount(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white text-sm rounded-lg border border-gray-600 focus:ring-2 focus:ring-[#00C853] focus:border-transparent"
                >
                  <option value="all">Wszystkie konta</option>
                  {accounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.page_name} ({account.platform})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-1">Status odpowiedzi</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white text-sm rounded-lg border border-gray-600 focus:ring-2 focus:ring-[#00C853] focus:border-transparent"
                >
                  <option value="all">Wszystkie</option>
                  <option value="pending">Bez odpowiedzi</option>
                  <option value="handled">Z odpowiedzia</option>
                </select>
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-1">Status kolejki</label>
                <select
                  value={selectedQueueStatus}
                  onChange={(e) => setSelectedQueueStatus(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white text-sm rounded-lg border border-gray-600 focus:ring-2 focus:ring-[#00C853] focus:border-transparent"
                >
                  <option value="all">Wszystkie</option>
                  <option value="pending_approval">Oczekujace na zatwierdzenie</option>
                  <option value="dlq">Bledy (DLQ)</option>
                  <option value="queued">W kolejce</option>
                  <option value="sent">Wyslane</option>
                  <option value="no_queue">Bez kolejki</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-[#00C853] focus:ring-[#00C853] focus:ring-offset-gray-800"
                />
                <span className="text-sm text-gray-300">Pokaz zarchiwizowane</span>
              </label>

              {activeFiltersCount > 0 && (
                <button
                  onClick={() => {
                    setSelectedPlatform('all');
                    setSelectedStatus('all');
                    setSelectedQueueStatus('all');
                    setSelectedAccount('all');
                    setShowArchived(false);
                  }}
                  className="text-sm text-[#00C853] hover:text-[#00E676] transition-colors"
                >
                  Wyczysc filtry
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between text-sm text-gray-400">
        <div className="flex items-center space-x-4">
          {!showArchived && filteredComments.length > 0 && (
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedComments.size === filteredComments.length && filteredComments.length > 0}
                onChange={toggleSelectAll}
                className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-[#00C853] focus:ring-[#00C853] focus:ring-offset-gray-800"
              />
              <span>Zaznacz wszystkie</span>
            </label>
          )}
        </div>
        <span>
          Wyswietlono {filteredComments.length} z {comments.length} komentarzy
          {searchQuery && ` (wyszukiwanie: "${searchQuery}")`}
        </span>
      </div>

      {filteredComments.length === 0 ? (
        <div className="bg-gray-800 rounded-lg p-12 text-center">
          <MessageSquare className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            {showArchived ? 'Brak zarchiwizowanych komentarzy' : 'Brak komentarzy'}
          </h3>
          <p className="text-gray-400">
            {searchQuery
              ? `Nie znaleziono komentarzy dla "${searchQuery}"`
              : 'Nie znaleziono komentarzy pasujacych do wybranych filtrow'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredComments.map((comment) => {
            const account = accounts.find(a => a.id === comment.account_id);
            const isReplying = replyingTo === comment.id;
            const isSelected = selectedComments.has(comment.id);

            return (
              <div
                key={comment.id}
                className={`bg-gray-800 rounded-lg p-6 border transition-colors ${
                  isSelected
                    ? 'border-[#00C853]'
                    : 'border-gray-700 hover:border-[#00C853]/50'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    {!showArchived && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleCommentSelection(comment.id)}
                        className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-[#00C853] focus:ring-[#00C853] focus:ring-offset-gray-800"
                      />
                    )}
                    <div className="p-2 bg-gray-700 rounded-lg">
                      {getPlatformIcon(comment.platform)}
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold text-white">
                          {comment.from_user?.name || 'Nieznany uzytkownik'}
                        </span>
                        <span className="text-gray-500 text-sm">-</span>
                        <span className="text-gray-400 text-sm capitalize">
                          {comment.platform}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-xs text-gray-500">
                          {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                        </span>
                        {account && (
                          <>
                            <span className="text-gray-600">-</span>
                            <span className="text-xs text-gray-500">
                              {account.page_name}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusBadge(comment)}
                    <button
                      onClick={() => showArchived ? handleUnarchive(comment) : handleArchive(comment)}
                      disabled={archivingId === comment.id}
                      className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                      title={showArchived ? 'Przywroc z archiwum' : 'Archiwizuj'}
                    >
                      {archivingId === comment.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : showArchived ? (
                        <ArchiveRestore className="w-4 h-4" />
                      ) : (
                        <Archive className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="bg-gray-900 rounded-lg p-4 mb-4">
                  <p className="text-white whitespace-pre-wrap">{comment.text}</p>
                </div>

                {getPostUrl(comment) && (
                  <a
                    href={getPostUrl(comment)!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 text-sm text-[#00C853] hover:text-[#00E676] mb-3 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>Zobacz {comment.platform === 'youtube' ? 'film' : 'post'}</span>
                  </a>
                )}

                {comment.our_reply_text && (
                  <div className={`rounded-lg p-4 mb-4 border-l-4 ${
                    comment.our_reply_id
                      ? 'bg-gray-900/50 border-[#00C853]'
                      : comment.queue_status === 'DLQ'
                        ? 'bg-yellow-900/20 border-yellow-500'
                        : comment.queue_status === 'PENDING_APPROVAL'
                          ? 'bg-orange-900/20 border-orange-500'
                          : 'bg-blue-900/20 border-blue-500'
                  }`}>
                    <div className="flex items-center space-x-2 mb-2">
                      {comment.our_reply_id ? (
                        <>
                          <CheckCircle className="w-4 h-4 text-green-400" />
                          <span className="text-sm font-semibold text-green-400">Twoja odpowiedz:</span>
                        </>
                      ) : comment.queue_status === 'DLQ' ? (
                        <>
                          <AlertCircle className="w-4 h-4 text-yellow-400" />
                          <span className="text-sm font-semibold text-yellow-400">Odpowiedz AI (nie wyslana):</span>
                        </>
                      ) : comment.queue_status === 'PENDING_APPROVAL' ? (
                        <>
                          <Clock className="w-4 h-4 text-orange-400" />
                          <span className="text-sm font-semibold text-orange-400">Odpowiedz AI (oczekuje na zatwierdzenie):</span>
                        </>
                      ) : (
                        <>
                          <Clock className="w-4 h-4 text-blue-400" />
                          <span className="text-sm font-semibold text-blue-400">Odpowiedz AI (w kolejce):</span>
                        </>
                      )}
                    </div>
                    <p className="text-gray-300 whitespace-pre-wrap">{comment.our_reply_text}</p>
                  </div>
                )}

                {comment.queue_status === 'DLQ' && comment.queue_last_error && (
                  <div className="bg-red-900/20 rounded-lg p-4 mb-4 border-l-4 border-red-500">
                    <div className="flex items-center space-x-2 mb-2">
                      <AlertCircle className="w-4 h-4 text-red-400" />
                      <span className="text-sm font-semibold text-red-400">Blad:</span>
                    </div>
                    <p className="text-gray-300 text-sm whitespace-pre-wrap">{comment.queue_last_error}</p>
                  </div>
                )}

                {comment.queue_status === 'DLQ' && (
                  <button
                    onClick={() => handleRetry(comment)}
                    className="flex items-center space-x-2 px-4 py-2 bg-[#00C853] text-white rounded-lg hover:bg-[#00E676] transition-colors mb-4"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>Ponow</span>
                  </button>
                )}

                {comment.queue_status === 'PENDING_APPROVAL' && (
                  <div className="flex items-center space-x-3 mb-4">
                    <button
                      onClick={() => handleApprove(comment)}
                      disabled={approvingId === comment.id || rejectingId === comment.id}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Opublikuj odpowiedz AI jako komentarz na platformie"
                    >
                      {approvingId === comment.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <ThumbsUp className="w-4 h-4" />
                      )}
                      <span>{approvingId === comment.id ? 'Zatwierdzanie...' : 'Opublikuj'}</span>
                    </button>
                    <button
                      onClick={() => handleReject(comment)}
                      disabled={approvingId === comment.id || rejectingId === comment.id}
                      className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Odrzuc odpowiedz AI i oznacz komentarz jako obsluzony"
                    >
                      {rejectingId === comment.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <X className="w-4 h-4" />
                      )}
                      <span>{rejectingId === comment.id ? 'Odrzucanie...' : 'Odrzuc'}</span>
                    </button>
                  </div>
                )}

                {!comment.handled && comment.queue_status !== 'DLQ' && comment.queue_status !== 'PENDING_APPROVAL' && (
                  <>
                    {isReplying ? (
                      <div className="space-y-3">
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Wpisz swoja odpowiedz..."
                          rows={3}
                          className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg border border-gray-700 focus:ring-2 focus:ring-[#00C853] focus:border-transparent resize-none"
                        />
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleReply(comment)}
                            disabled={sendingReply || !replyText.trim()}
                            className="flex items-center space-x-2 px-4 py-2 bg-[#00C853] text-white rounded-lg hover:bg-[#00E676] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {sendingReply ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                            <span>{sendingReply ? 'Wysylanie...' : 'Wyslij odpowiedz'}</span>
                          </button>
                          <button
                            onClick={() => {
                              setReplyingTo(null);
                              setReplyText('');
                            }}
                            disabled={sendingReply}
                            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                          >
                            Anuluj
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setReplyingTo(comment.id)}
                          className="flex items-center space-x-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                        >
                          <MessageSquare className="w-4 h-4" />
                          <span>{comment.is_reply_to_other_user ? 'Odpisz recznie' : 'Odpowiedz'}</span>
                        </button>
                        {comment.is_reply_to_other_user && (
                          <button
                            className="flex items-center space-x-2 px-4 py-2 bg-[#00C853] text-white rounded-lg hover:bg-[#00E676] transition-colors"
                            title="Uzyj agenta AI do wygenerowania odpowiedzi"
                          >
                            <Send className="w-4 h-4" />
                            <span>Odpisz Agentem</span>
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
